import { createDebug } from '@/utils/debug'

// AHA-43111: the read-view bridge for PowerPoint Online, kept isolated from services/office
// because it is the ONE binding store that is web-slideshow-specific. Starting Slide Show on
// the web spins up a BRAND-NEW session where PowerPoint.run (the Rich API) throws
// `RichApi.Error` and document.settings come up empty — so neither the host-slide tag nor the
// last-selected tag can be read, and the presenting cold-load falls through to the presentation
// list. localStorage is origin-scoped and DOES survive the slideshow reload, and document.url is
// a Common-API property that still works in the read view, so we bridge the current binding
// through localStorage keyed by the document URL.
//
// Keying by document is essential: an un-keyed value would leak across every .pptx opened in
// this browser (the exact reason the cross-frame store in services/office is a presentation TAG,
// not localStorage), so presenting document B would resume document A's binding. Every entry
// point is web-only via isPptOnlineAddin(): a no-op on desktop, which resolves from baked
// settings and never hits this path.
//
// STORES ONLY — durable bindings, written in the edit session and read back in the show. The
// live "which slide is on screen" channel that the show broadcasts between frames is a different
// concern on the same transport and lives in ppt-online-current-slide; it reuses the gate and the
// document-scoped key builder below.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:online-binding' (or 'aha:ppt:*').
const log = createDebug('ppt:online-binding')

// Resolved lazily rather than via a static `import`, for the same reason debug.js does it:
// ppt-online.utils imports the store, the store's presentation module imports services/office,
// and services/office imports this file — a static import here closes that cycle. Every call
// site below runs at runtime, long after every module has finished evaluating, so a lazy
// require is safe where a static import would leave the binding undefined at init time.
// Exported so ppt-online-current-slide can reuse it rather than repeat the lazy-require dance;
// that module sits behind the same cycle and needs the same gate.
let isPptOnlineAddinFn = null
export const isPptOnlineAddin = () => {
  if (!isPptOnlineAddinFn) {
    try {
      // eslint-disable-next-line global-require
      isPptOnlineAddinFn = require('@/utils/ppt-online.utils').isPptOnlineAddin
    } catch (error) {
      isPptOnlineAddinFn = () => false
    }
  }
  return isPptOnlineAddinFn()
}

// Every key this add-in puts in localStorage is scoped by document.url — a Common-API property
// that survives into the Slide Show session and differs per .pptx. Un-scoped keys would leak
// across every deck opened in this browser, resolving document B to document A's binding.
// Returns null (and says so) when the host exposes no URL, which is the one case where writing
// anything at all would be unsafe. Shared with ppt-online-current-slide.
export const documentScopedKey = (prefix) => {
  const url = window.Office?.context?.document?.url
  if (!url) {
    log('no document.url -> skipping (cannot key safely)')
    return null
  }
  return `${prefix}${url}`
}

const KEY_PREFIX = 'aha:ppt:online-binding:'

const getBindingKey = () => documentScopedKey(KEY_PREFIX)

// `value` is the same {presentationId, slideId} / {activePresentationId} shape as dataSlidePPT.
// A falsy presentationId (the unbind case) clears the entry rather than storing a dead binding.
export const savePptOnlineBinding = (value) => {
  if (!isPptOnlineAddin()) return
  const key = getBindingKey()
  if (!key) return
  try {
    const presentationId = value && (value.activePresentationId || value.presentationId)
    if (!presentationId) {
      localStorage.removeItem(key)
      log('cleared |', key)
      return
    }
    const slideId = (value && value.slideId) || null
    localStorage.setItem(key, JSON.stringify({ presentationId, slideId }))
    log('saved |', key, '| presentationId =', presentationId, '| slideId =', slideId)
  } catch (error) {
    log('save failed', error)
  }
}

// Returns { presentationId, slideId } or null. slideId may be null (whole-presentation binding).
export const loadPptOnlineBinding = () => {
  if (!isPptOnlineAddin()) return null
  const key = getBindingKey()
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && parsed.presentationId ? parsed : null
  } catch (error) {
    log('load failed', error)
    return null
  }
}

// AHA-43111 per-slide read-view bridge. The doc-URL binding above holds ONE document-wide
// value, so a deck with several DISTINCT AhaSlides slides collapses to a single binding and
// the Slide Show resolves every slide to it. This second store is keyed by the slide's
// Common-API id (getSelectedDataAsync(SlideRange).id) so each physical slide carries its own
// binding — the probe confirmed that id is IDENTICAL for a given slide across the edit session
// (where we write) and the Slide Show session (where we read). Still document-scoped: the
// Common-API id (256, 257, …) restarts per .pptx, so an un-namespaced key would collide across
// documents opened in this browser.
const COMMON_KEY_PREFIX = 'aha:ppt:common-slide:'

const getCommonSlideKey = (commonSlideId) => {
  if (commonSlideId === null || commonSlideId === undefined || commonSlideId === '') {
    log('no common slide id -> skipping (cannot key)')
    return null
  }
  const documentKey = documentScopedKey(COMMON_KEY_PREFIX)
  return documentKey && `${documentKey}::${commonSlideId}`
}

// `value` is the dataSlidePPT shape { presentationId, slideId }. Only a complete single-slide
// binding is stored; a missing presentationId/slideId (the unbind case, or a whole-presentation
// binding that has no single slide) CLEARS the entry so the Slide Show never resolves a slide
// the binding no longer points at.
export const savePptCommonSlideBinding = (commonSlideId, value) => {
  if (!isPptOnlineAddin()) {
    // Symmetric with the load side: a web frame writing during an insert before canUsePptOnline
    // has resolved would silently skip, and the only visible symptom would be an empty bridge
    // several minutes later in the Slide Show.
    log('save common-slide skipped | not a PowerPoint Online add-in (or flag not ready) | id =', commonSlideId)
    return
  }
  const key = getCommonSlideKey(commonSlideId)
  if (!key) return
  try {
    const presentationId = value && value.presentationId
    const slideId = value && value.slideId
    if (!presentationId || !slideId) {
      localStorage.removeItem(key)
      log('cleared common-slide |', key)
      return
    }
    localStorage.setItem(key, JSON.stringify({ presentationId, slideId }))
    log('saved common-slide |', key, '| presentationId =', presentationId, '| slideId =', slideId)
  } catch (error) {
    log('save common-slide failed', error)
  }
}

// Returns { presentationId, slideId } for the given Common-API slide id, or null.
export const loadPptCommonSlideBinding = (commonSlideId) => {
  if (!isPptOnlineAddin()) {
    // Worth logging: off-web this is a no-op, but so is a web frame whose canUsePptOnline flag
    // has not resolved yet — and in the Slide Show that reads as "the bridge is empty".
    log('load common-slide skipped | not a PowerPoint Online add-in (or flag not ready)')
    return null
  }
  const key = getCommonSlideKey(commonSlideId)
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      log('MISS | looked for', key)
      return null
    }
    log('HIT |', key, '=>', raw)
    const parsed = JSON.parse(raw)
    return parsed && parsed.presentationId ? parsed : null
  } catch (error) {
    log('load common-slide failed', error)
    return null
  }
}
